import { Component, Input } from '@angular/core';
import { AppService } from '../../../services/app.service';
import { ITableColumn } from '../../../shared/table/table.component';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { IFilterOptions, IItem, defaultFilterOptions } from '../health-ministry-dashboard.component';
import { NavigationStart, Router, RouterEvent } from '@angular/router';
import { Utils } from '../../../core/Utils';

interface IFilterValues {
    dateFrom?: Date;
    dateTo?: Date;
    search?: string;
    status?: string;
    speciality?: string;
    studyYear?: number;
    workplace?: string;
    clinicalBase?: string;
    studyType?: string;
    financeType?: string;
}

const cache: {
    filters?: IFilterValues,
    sortBy?: string,
    sortDir?: 'asc' | 'desc',
    page?: number
} = {};

function clearCache() {
    cache.filters = undefined;
    cache.sortBy = undefined;
    cache.sortDir = undefined;
    cache.page = undefined;
}

let isRouterSubscribed = false;

@Component({
    selector: 'app-health-ministry-dashboard-residents',
    templateUrl: './health-ministry-dashboard-residents.component.html',
    styleUrls: ['./health-ministry-dashboard-residents.component.scss']
})
export class HealthMinistryDashboardResidentsComponent {
    constructor(
        private readonly app: AppService,
        private readonly router: Router
    ) { }

    @Input() data: IItem[] = [];
    @Input() filters: IFilterOptions = defaultFilterOptions;

    rows: IItem[] = [];
    page: number = cache.page || 1;
    pageSize: number = 10;
    sortBy: string = cache.sortBy;
    sortDir: 'asc' | 'desc' = cache.sortDir;

    readonly columns: ITableColumn[] = [
        {
            property: 'Name',
            label: 'healthMinistryDashboard_lblNameSurnameId',
            sorts: true,
            sortFn: (a: IItem, b: IItem) => {
                return a.title.localeCompare(b.title, this.app.currentLanguage);
            }
        },
        { property: 'StudyProgramName', label: 'healthMinistryDashboard_lblSpeciality', sorts: true },
        { property: 'StudentStatusName', label: 'healthMinistryDashboard_lblResidentStatus', sorts: true },
        { property: 'StudyYear', label: 'healthMinistryDashboard_lblStudyYear', sorts: true, type: 'number' },
        { property: 'StudyBeginDate', label: 'healthMinistryDashboard_lblStudyBeginDate', sorts: true, type: 'date' },
        { property: 'StudyPlannedEndDate', label: 'healthMinistryDashboard_lblStudyEndDate', sorts: true, type: 'date' },
        { property: 'WorkplaceName', label: 'healthMinistryDashboard_lblWorkplace', sorts: true },
        { property: 'ClinicalBaseName', label: 'healthMinistryDashboard_lblClinicalBase', sorts: true },
        { property: 'StudyTypeName', label: 'healthMinistryDashboard_lblFinancing', sorts: true },
        { property: 'StudyFinanceTypeName', label: 'healthMinistryDashboard_lblFinancingType', sorts: true }
    ];

    filterValues: IFilterValues = {};

    private all: IItem[] = [];
    private prevFilter: string;
    private readonly filterSubj = new Subject();

    private readonly exportColumns = [
        this.app.translate('healthMinistryDashboard_lblName'),
        this.app.translate('healthMinistryDashboard_lblSurname'),
        this.app.translate('healthMinistryDashboard_lblId'),
        this.app.translate('healthMinistryDashboard_lblSpeciality'),
        this.app.translate('healthMinistryDashboard_lblResidentStatus'),
        this.app.translate('healthMinistryDashboard_lblStudyYear'),
        this.app.translate('healthMinistryDashboard_lblStudyBeginDate'),
        this.app.translate('healthMinistryDashboard_lblStudyEndDate'),
        this.app.translate('healthMinistryDashboard_lblWorkplace'),
        this.app.translate('healthMinistryDashboard_lblClinicalBase'),
        this.app.translate('healthMinistryDashboard_lblFinancing'),
        this.app.translate('healthMinistryDashboard_lblFinancingType')
    ];

    ngOnInit() {
        if (!isRouterSubscribed) {
            isRouterSubscribed = true;
            this.router.events.subscribe((e: RouterEvent) => {
                if (e.url && e instanceof NavigationStart && !e.url.toLowerCase().includes('/health-ministry-dashboard')) {
                    clearCache();
                }
            });
        }

        this.all = this.data;
        this.filterValues = cache.filters || {};

        this.filterSubj.pipe(debounceTime(300)).subscribe(term => {
            this.filter();
        });

        this.filter();
    }

    filter(delay: boolean = false) {
        this.page = 1;

        if (delay) {
            this.filterSubj.next();
        } else {
            cache.filters = this.filterValues;

            const s = this.filterValues.search?.toLowerCase();
            const from = this.filterValues.dateFrom;
            const to = this.filterValues.dateTo;
            const status = this.filterValues.status;
            const spec = this.filterValues.speciality;
            const year = this.filterValues.studyYear;
            const workplace = this.filterValues.workplace;
            const base = this.filterValues.clinicalBase;
            const studyType = this.filterValues.studyType;
            const financeType = this.filterValues.financeType;

            const next = [
                s,
                from,
                to,
                status,
                spec,
                year,
                workplace,
                base,
                studyType,
                financeType
            ].join(';;');

            if (next == this.prevFilter) {
                return;
            }

            this.prevFilter = next;

            if (s || from || to || status || spec || year || workplace || base || studyType || financeType) {
                this.rows = this.all.filter(t => {
                    if (from && t.dateFrom < from) return false;
                    if (to && t.dateTo > to) return false;
                    if (status && t.StudentStatusName != status) return false;
                    if (spec && t.StudyProgramName != spec) return false;
                    if (year && t.StudyYear != year) return false;
                    if (workplace && t.WorkplaceName != workplace) return false;
                    if (base && t.ClinicalBaseName != base) return false;
                    if (studyType && t.StudyTypeName != studyType) return false;
                    if (financeType && t.StudyFinanceTypeName != financeType) return false;
                    if (s && !t.title.toLowerCase().includes(s)) return false;
                    return true;
                });
            } else {
                this.rows = this.all;
            }
        }
    }

    clearFilter() {
        this.filterValues.clinicalBase = undefined;
        this.filterValues.dateFrom = undefined;
        this.filterValues.dateTo = undefined;
        this.filterValues.financeType = undefined;
        this.filterValues.search = undefined;
        this.filterValues.speciality = undefined;
        this.filterValues.status = undefined;
        this.filterValues.studyType = undefined;
        this.filterValues.studyYear = undefined;
        this.filterValues.workplace = undefined;

        this.filter();
    }

    onPage(page: number) {
        this.page = cache.page = page;
    }

    onSort(sort: { sortBy: string, sortDir: 'asc' | 'desc' }) {
        this.sortBy = cache.sortBy = sort.sortBy;
        this.sortDir = cache.sortDir = sort.sortDir;
    }

    export() {
        const data = [this.exportColumns, ...this.rows.map(t => {
            return [
                t.Name,
                t.Surname,
                t.CertNumber,
                t.StudyProgramName,
                t.StudentStatusName,
                t.StudyYear,
                t.StudyBeginDate,
                t.StudyPlannedEndDate,
                t.WorkplaceName,
                t.ClinicalBaseName,
                t.StudyTypeName,
                t.StudyFinanceTypeName
            ];
        })];

        Utils.arrayToXlsx(data, 'health_ministry_residents');
    }
}
